import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TrackedButton from '../components/TrackedButton';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`signup-tabpanel-${index}`}
      aria-labelledby={`signup-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SignUp: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  // Publisher form state
  const [publisherName, setPublisherName] = useState('');
  const [publisherEmail, setPublisherEmail] = useState('');
  const [publisherPassword, setPublisherPassword] = useState('');
  const [publisherConfirmPassword, setPublisherConfirmPassword] = useState('');
  const [publisherId, setPublisherId] = useState<string | null>(null);

  // Advertiser form state
  const [advertiserName, setAdvertiserName] = useState('');
  const [advertiserEmail, setAdvertiserEmail] = useState('');
  const [advertiserPassword, setAdvertiserPassword] = useState('');
  const [advertiserConfirmPassword, setAdvertiserConfirmPassword] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const handlePublisherSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPublisherId(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/publishers/register`, {
        name: publisherName,
        email: publisherEmail,
        password: publisherPassword,
        user_type: "publisher"
      });
      setPublisherId(res.data.publisher_id);
      setSuccess('Publisher registration successful! Please copy your Publisher ID before proceeding.');
      // Redirect to login page after successful registration
      setTimeout(() => navigate('/login'), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvertiserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await axios.post(`${API_URL}/advertisers/register`, {
        name: advertiserName,
        email: advertiserEmail,
        password: advertiserPassword,
        user_type: "advertiser"
      });
      setSuccess('Advertiser registration successful!');
      // Redirect to login or dashboard after successful registration
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Card sx={{ minWidth: 400, maxWidth: 600, width: '100%' }}>
        <CardContent sx={{ p: 0 }}>
          <Paper square>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Publisher" />
              <Tab label="Advertiser" />
            </Tabs>
          </Paper>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="h5" gutterBottom>Publisher Sign Up</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Register to receive your unique publisher ID for SDK integration.
            </Typography>
            <form onSubmit={handlePublisherSubmit}>
              <TextField
                label="Name"
                value={publisherName}
                onChange={e => setPublisherName(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Email"
                type="email"
                value={publisherEmail}
                onChange={e => setPublisherEmail(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Password"
                type="password"
                value={publisherPassword}
                onChange={e => setPublisherPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={publisherConfirmPassword}
                onChange={e => setPublisherConfirmPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                error={publisherPassword !== publisherConfirmPassword && publisherConfirmPassword !== ''}
                helperText={publisherPassword !== publisherConfirmPassword && publisherConfirmPassword !== '' ? "Passwords don't match" : ""}
              />
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
              {publisherId && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <span>Your publisher ID: <b>{publisherId}</b></span>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        navigator.clipboard.writeText(publisherId);
                        setSuccess('Publisher ID copied to clipboard!');
                      }} 
                      sx={{ ml: 1 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Alert>
              )}
              <TrackedButton
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading || publisherPassword !== publisherConfirmPassword}
                trackingName="publisher_signup"
                trackingCategory="signup"
                trackingData={{ user_type: "publisher" }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign Up'}
              </TrackedButton>
            </form>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h5" gutterBottom>Advertiser Sign Up</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Create an account to start advertising with Reasonic.
            </Typography>
            <form onSubmit={handleAdvertiserSubmit}>
              <TextField
                label="Name"
                value={advertiserName}
                onChange={e => setAdvertiserName(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Email"
                type="email"
                value={advertiserEmail}
                onChange={e => setAdvertiserEmail(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Password"
                type="password"
                value={advertiserPassword}
                onChange={e => setAdvertiserPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={advertiserConfirmPassword}
                onChange={e => setAdvertiserConfirmPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                error={advertiserPassword !== advertiserConfirmPassword && advertiserConfirmPassword !== ''}
                helperText={advertiserPassword !== advertiserConfirmPassword && advertiserConfirmPassword !== '' ? "Passwords don't match" : ""}
              />
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
              <TrackedButton
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                disabled={loading || advertiserPassword !== advertiserConfirmPassword}
                trackingName="advertiser_signup"
                trackingCategory="signup"
                trackingData={{ user_type: "advertiser" }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign Up'}
              </TrackedButton>
            </form>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SignUp; 